import React from 'react'
import styled from 'styled-components'

import AvatarStack from 'components/AvatarStack'
import TimeAgo, { units as timeUnits } from 'components/TimeAgo'
import DocumentCardIcons from 'components/DocumentCard/Icons/DocumentCardIcons'
import Dropdown from 'components/Dropdown'
import Tooltip from 'components/Tooltip'
import PersonAvatar from 'components/PersonAvatar'

import { ReactComponent as CloudUpload } from 'icons/cloud-arrow-up-64.svg'
import { ReactComponent as DocumentProcessing } from 'icons/document-placeholder-64.svg'
import { ReactComponent as WarningTriangle } from 'icons/warning-triangle-64.svg'
import { ReactComponent as ImagePlaceholder } from 'icons/image-placeholder-64.svg'

import { RenderStatus } from 'utils/renderStatus'
import { DocumentSharingStatus } from '../../utils/getDocumentSharingStatus'

import {
  ImageWrapper,
  ImageElement,
  GridWrapper,
  TableWrapper,
  Name,
  Image,
  StatusIcon,
  SubtitleTextWrapper,
  TableCell,
  DropdownButton,
  DropdownButtonWrapper,
  DropdownButtonEllipsis,
  AvatarTooltipWrapper,
  NameWrapper,
  DropdownWrapper,
  TableAvatarNames,
  GridPlaceholderWrapper,
  TablePlaceholderWrapper,
} from './DocumentItem.styles'
import LoadingPlaceholder from 'components/LoadingPlaceholder'

export interface AvatarItem {
  name: string
  avatar: string
  identifier: string
}

export interface DocumentItemProps {
  className?: string
  name: string
  renderStatus: RenderStatus
  thumbnailSource?: string
  lastUpdatedDate: string
  documentSharingStatus: DocumentSharingStatus
  projectName?: string

  isEditing?: boolean
  isLibrary?: boolean
  onClick?: React.MouseEventHandler<HTMLElement>

  renderDropdownItems?: () => React.ReactNode
  renderAvatarItems: () => AvatarItem[]
}

const handleTimeFormat = (value: string, unit: keyof typeof timeUnits) => {
  if (unit === 'second') return 'Now'

  return `${value}${timeUnits[unit]} ago`
}

const renderImage = (props: DocumentItemProps, layout: 'grid' | 'list') => {
  const { name, renderStatus, thumbnailSource } = props

  let image
  let tooltipMessage = name

  switch (renderStatus) {
    case 'failed':
      tooltipMessage = `"${name}" - Processing failed.`

      image = (
        <StatusIcon as={WarningTriangle} aria-label="Processing Failed icon" />
      )
      break

    case 'loading':
    case 'uploading':
      tooltipMessage = `"${name}" - Uploading document…`

      image = (
        <StatusIcon as={CloudUpload} aria-label="Uploading to cloud icon" />
      )
      break

    case 'ready': {
      if (!thumbnailSource) {
        image = (
          <StatusIcon as={ImagePlaceholder} aria-label="No Image available" />
        )
      } else {
        image = (
          <Image
            alt={name}
            customImageElement={ImageElement}
            src={thumbnailSource}
            loadingPlaceholder={
              layout === 'grid' ? (
                <LoadingPlaceholder size="64px" />
              ) : (
                <LoadingPlaceholder size="16px" />
              )
            }
          />
        )
      }

      break
    }

    case 'processing':
      tooltipMessage = `"${name}" - Processing document…`

      image = (
        <StatusIcon as={DocumentProcessing} aria-label="Processing icon" />
      )
      break
  }

  return { image, tooltipMessage }
}

const renderDropdown = (dropdownContent: React.ReactNode) => (
  <DropdownWrapper onClick={event => event.stopPropagation()}>
    <Dropdown
      spacing="0px"
      placement="bottom-end"
      usePortal
      toggle={() => (
        <DropdownButton>
          <DropdownButtonEllipsis aria-label="Document Settings" />
          <span className="sr-only">Document Settings</span>
        </DropdownButton>
      )}>
      {dropdownContent}
    </Dropdown>
  </DropdownWrapper>
)

const getButtonProps = (
  handleOnClick: (event: React.MouseEvent<HTMLElement>) => void
) => ({
  role: 'button',
  tabIndex: 0,
  onClick: handleOnClick,
  onKeyPress: (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      /* Converge for the same type of event */
      handleOnClick((event as unknown) as React.MouseEvent<HTMLElement>)
    }
  },
})

export const DocumentGridItem = styled(function DocumentGridItem(
  props: DocumentItemProps
) {
  const {
    className,
    name,
    documentSharingStatus,
    projectName,
    lastUpdatedDate,
    isEditing,
    isLibrary,
    renderDropdownItems,
    renderAvatarItems,
    onClick,
  } = props

  const { image, tooltipMessage } = renderImage(props, 'grid')
  const dropdownContent = renderDropdownItems?.()
  const avatars = renderAvatarItems()
  const a11yProps = onClick ? getButtonProps(onClick) : {}

  const content = (
    <>
      {isEditing ? 'Being edited by' : 'Last save from'}
      {avatars.map(({ identifier, name, avatar }) => (
        <AvatarTooltipWrapper key={identifier}>
          <PersonAvatar flavour="image" src={avatar} name={name} size="16px" />
          {name}
        </AvatarTooltipWrapper>
      ))}
    </>
  )

  return (
    <GridWrapper className={className} title={name} {...a11yProps}>
      <ImageWrapper title={tooltipMessage}>{image}</ImageWrapper>
      <Name>
        {name}
        <DocumentCardIcons
          isLibrary={isLibrary}
          documentSharingStatus={documentSharingStatus}
        />
      </Name>
      <SubtitleTextWrapper>
        {projectName && <b className="shrink">{projectName}</b>}
        <span className="keep-size">
          {isEditing ? (
            'Editing'
          ) : (
            <TimeAgo date={lastUpdatedDate} formatter={handleTimeFormat} />
          )}
        </span>
        <Tooltip
          placement="bottom-start"
          content={content}
          spacing="3px"
          scrollable>
          <AvatarStack
            size="16px"
            items={avatars}
            renderItems={({ avatar, identifier, name }) => [
              identifier,
              { name, flavour: 'image', src: avatar },
            ]}
          />
        </Tooltip>
      </SubtitleTextWrapper>

      {dropdownContent && (
        <DropdownButtonWrapper>
          {renderDropdown(dropdownContent)}
        </DropdownButtonWrapper>
      )}
    </GridWrapper>
  )
})``

export const DocumentListItem = styled(function DocumentListItem(
  props: DocumentItemProps
) {
  const {
    className,
    name,
    documentSharingStatus,
    projectName,
    isEditing,
    isLibrary,
    lastUpdatedDate,
    renderDropdownItems,
    renderAvatarItems,
    onClick,
  } = props

  const { image, tooltipMessage } = renderImage(props, 'list')
  const dropdownContent = renderDropdownItems?.()
  const avatars = renderAvatarItems()

  const a11yProps = onClick ? getButtonProps(onClick) : {}

  return (
    <TableWrapper className={className} title={name} {...a11yProps}>
      <TableCell>
        <NameWrapper>
          <ImageWrapper title={tooltipMessage}>{image}</ImageWrapper>
          <Name>
            {name}
            <DocumentCardIcons
              isLibrary={isLibrary}
              documentSharingStatus={documentSharingStatus}
            />
          </Name>
        </NameWrapper>
      </TableCell>
      <TableCell className="wrap">
        <span>{projectName || '—'}</span>
      </TableCell>
      <TableCell>
        <span>
          {isEditing ? (
            'Editing'
          ) : (
            /* TODO: https://github.com/sketch-hq/Cloud/issues/9044 */
            /* The time format needs to be adjusted to show date in a format "Yesterday at 5:32pm" */
            <TimeAgo date={lastUpdatedDate} formatter={handleTimeFormat} />
          )}
        </span>
      </TableCell>
      <TableCell>
        <NameWrapper>
          <AvatarStack
            size="16px"
            items={avatars}
            renderItems={({ avatar, identifier, name }) => [
              identifier,
              { name, flavour: 'image', src: avatar },
            ]}
          />

          <TableAvatarNames>
            {avatars.map(({ name }) => name).join(', ')}
          </TableAvatarNames>
        </NameWrapper>
      </TableCell>
      <TableCell>
        {dropdownContent && renderDropdown(dropdownContent)}
      </TableCell>
    </TableWrapper>
  )
})``

interface PlaceholderProps {
  inViewListener?: React.ReactNode
}

export const DocumentGridItemPlaceholder: React.FC<PlaceholderProps> = props => (
  <GridPlaceholderWrapper>
    <ImageWrapper />
    {props.inViewListener}
  </GridPlaceholderWrapper>
)

export const DocumentTableItemPlaceholder: React.FC<PlaceholderProps> = props => (
  <TablePlaceholderWrapper>
    <TableCell>
      <ImageWrapper />
      {props.inViewListener}
    </TableCell>

    {/* Placeholder cells to make sure the table is correctly rendered */}
    <TableCell />
    <TableCell />
    <TableCell />
    <TableCell />
  </TablePlaceholderWrapper>
)
